import { useAuth } from "@allergy123/auth-client/react";
import { Button, Container, DraftBanner, SiteFooter, SiteHeader } from "@allergy123/ui";
import { useEffect, type ReactNode } from "react";
import { NavLink, ScrollRestoration, useNavigate } from "react-router";
import { RunningTimersBar } from "./encounter/RunningTimers";
import { inAppPath } from "./lib/links";
import { useSession } from "./session";

/** Routes same-origin plain-anchor clicks through the router, so moving between screens keeps the tab's timers (lib/links.ts). */
function useInAppLinks() {
  const navigate = useNavigate();
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = e.target instanceof Element ? e.target.closest("a[href]") : null;
      const path = inAppPath(e, a instanceof HTMLAnchorElement ? { href: a.href, target: a.target, download: a.hasAttribute("download") } : null, window.location.origin);
      if (path === null) return;
      e.preventDefault();
      navigate(path);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);
}

export function ClinicShell({ children }: { children: ReactNode }) {
  const { session, signOut } = useAuth();
  const s = useSession();
  useInAppLinks();
  const link = (to: string, label: string) => (
    <NavLink to={to} end={to === "/"} className={({ isActive }) => (isActive ? "font-semibold text-anchor" : undefined)}>
      {label}
    </NavLink>
  );
  const picker = "min-h-10 max-w-[14rem] rounded-sm border border-rule-strong bg-card px-2 text-small text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";
  return (
    <div className="min-h-screen bg-canvas">
      {import.meta.env.MODE !== "production" && <DraftBanner>Development build — synthetic practice sign-in against the local API; every patient here is invented.</DraftBanner>}
      <SiteHeader
        logoVariant="auto"
        nav={
          <>
            {link("/", "Today")}
            {link("/calendar", "Calendar")}
            {link("/patients", "Patients")}
            {link("/encounters", "Encounters")}
            {link("/reports", "Reports")}
            {link("/supplies", "Supplies")}
            {link("/settings", "Settings")}
          </>
        }
        actions={
          <>
            {s.me.organizations.length > 1 ? (
              <>
                <label htmlFor="practice-picker" className="sr-only">
                  Practice
                </label>
                <select id="practice-picker" className={picker} value={s.organizationId} onChange={(e) => s.switchOrganization(e.target.value)}>
                  {s.me.organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <span className="hidden text-small text-ink-secondary sm:inline">{s.organizationName}</span>
            )}
            {s.locations.length > 1 && (
              <>
                <label htmlFor="location-picker" className="sr-only">
                  Location
                </label>
                <select id="location-picker" className={picker} value={s.location?.locationId ?? ""} onChange={(e) => s.setLocation(e.target.value)}>
                  {s.locations.map((l) => (
                    <option key={l.locationId} value={l.locationId}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </>
            )}
            <span className="hidden text-small text-ink-secondary lg:inline">{session?.claims.email}</span>
            <Button variant="secondary" onClick={() => void signOut()}>
              Sign out
            </Button>
          </>
        }
      />
      <main>
        <Container className="py-10">{children}</Container>
      </main>
      <RunningTimersBar />
      <SiteFooter />
      {/* A new screen starts at the top (Back and Forward return to where you were): routed links no longer reload. */}
      <ScrollRestoration />
    </div>
  );
}
